import { useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import {
  adminChangePassword,
  adminUpdateEmail,
  adminUpdateSupportEmail,
  getSupportEmail,
  updateAdminSessionEmail,
} from '@/hooks/useAdminAuth';
import { sendTestTelegram } from '@/lib/email';
import CodeLinkCard from './CodeLinkCard';
import type { CodeLink } from '@/hooks/useAdminData';

type SettingsTabProps = {
  adminEmail: string;
  codeLink: CodeLink | null;
  onSaveCodeLink: (patch: Partial<CodeLink>) => Promise<boolean>;
  onEmailChanged: (email: string) => void;
};

type Feedback = { kind: 'ok' | 'error'; message: string } | null;

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function SettingsTab({
  adminEmail,
  codeLink,
  onSaveCodeLink,
  onEmailChanged,
}: SettingsTabProps) {
  const { t } = useTranslation();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPasswords, setShowPasswords] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [newAdminEmail, setNewAdminEmail] = useState(adminEmail);
  const [adminEmailPassword, setAdminEmailPassword] = useState('');
  const [adminEmailSaving, setAdminEmailSaving] = useState(false);
  const [adminEmailFeedback, setAdminEmailFeedback] = useState<Feedback>(null);

  const [supportEmail, setSupportEmail] = useState('');
  const [supportSaving, setSupportSaving] = useState(false);
  const [supportFeedback, setSupportFeedback] = useState<Feedback>(null);

  const [botSending, setBotSending] = useState(false);
  const [botFeedback, setBotFeedback] = useState<Feedback>(null);

  useEffect(() => {
    setNewAdminEmail(adminEmail);
  }, [adminEmail]);

  useEffect(() => {
    let active = true;
    (async () => {
      const email = await getSupportEmail();
      if (active) setSupportEmail(email);
    })();
    return () => {
      active = false;
    };
  }, []);

  const handleUpdateAdminEmail = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setAdminEmailFeedback(null);

    if (!adminEmailPassword.trim()) {
      setAdminEmailFeedback({ kind: 'error', message: t('admin.settings.errorEmpty') });
      return;
    }
    if (!emailPattern.test(newAdminEmail.trim())) {
      setAdminEmailFeedback({ kind: 'error', message: t('admin.settings.emailInvalid') });
      return;
    }

    setAdminEmailSaving(true);
    const result = await adminUpdateEmail(adminEmailPassword, newAdminEmail.trim());
    setAdminEmailSaving(false);

    if (!result.ok) {
      if (result.reason === 'current') {
        setAdminEmailFeedback({ kind: 'error', message: t('admin.settings.adminEmailErrorPassword') });
      } else if (result.reason === 'invalid') {
        setAdminEmailFeedback({ kind: 'error', message: t('admin.settings.emailInvalid') });
      } else if (result.reason === 'empty') {
        setAdminEmailFeedback({ kind: 'error', message: t('admin.settings.errorEmpty') });
      } else {
        setAdminEmailFeedback({ kind: 'error', message: t('admin.settings.errorGeneric') });
      }
      return;
    }

    const updated = updateAdminSessionEmail(newAdminEmail.trim());
    onEmailChanged(updated?.email ?? newAdminEmail.trim());
    setAdminEmailPassword('');
    setAdminEmailFeedback({ kind: 'ok', message: t('admin.settings.adminEmailSaved') });
  };

  const handleUpdateSupportEmail = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSupportFeedback(null);

    if (!emailPattern.test(supportEmail.trim())) {
      setSupportFeedback({ kind: 'error', message: t('admin.settings.emailInvalid') });
      return;
    }

    setSupportSaving(true);
    const result = await adminUpdateSupportEmail(supportEmail.trim());
    setSupportSaving(false);

    if (!result.ok) {
      setSupportFeedback({ kind: 'error', message: t('admin.settings.errorGeneric') });
      return;
    }

    setSupportFeedback({ kind: 'ok', message: t('admin.settings.supportEmailSaved') });
  };

  const handleTestBot = async () => {
    setBotSending(true);
    setBotFeedback(null);
    const result = await sendTestTelegram();
    setBotSending(false);
    if (result.ok) {
      setBotFeedback({
        kind: 'ok',
        message: `${t('admin.settings.telegramSuccess')} ${result.chatId}`.trim(),
      });
    } else {
      setBotFeedback({
        kind: 'error',
        message: `${t('admin.settings.telegramError')} ${result.error}`,
      });
    }
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSuccess(false);

    if (!current.trim() || !next.trim() || !confirm.trim()) {
      setError(t('admin.settings.errorEmpty'));
      return;
    }
    if (next.trim().length < 6) {
      setError(t('admin.settings.errorLength'));
      return;
    }
    if (next.trim() !== confirm.trim()) {
      setError(t('admin.settings.errorMismatch'));
      return;
    }

    setSubmitting(true);
    const result = await adminChangePassword(current, next);
    setSubmitting(false);

    if (!result.ok) {
      if (result.reason === 'current') setError(t('admin.settings.errorCurrent'));
      else if (result.reason === 'empty') setError(t('admin.settings.errorEmpty'));
      else setError(t('admin.settings.errorGeneric'));
      return;
    }

    setError(null);
    setSuccess(true);
    setCurrent('');
    setNext('');
    setConfirm('');
  };

  const baseInput =
    'w-full rounded-md border border-background-300 bg-background-50 px-3.5 py-2.5 pr-11 text-sm text-foreground-950 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100';

  const feedbackBox = (feedback: Feedback) =>
    feedback ? (
      <div
        className={`mt-4 flex items-start gap-2 rounded-md border px-3 py-2.5 text-sm ${
          feedback.kind === 'ok'
            ? 'border-primary-200 bg-primary-50 text-primary-900'
            : 'border-accent-200 bg-accent-50 text-accent-900'
        }`}
      >
        <i
          className={`mt-0.5 ${
            feedback.kind === 'ok' ? 'ri-checkbox-circle-line' : 'ri-error-warning-line'
          }`}
        ></i>
        <span>{feedback.message}</span>
      </div>
    ) : null;

  return (
    <div className="space-y-5">
      <div>
        <h1 className="font-heading text-xl font-semibold text-foreground-950">
          {t('admin.settings.title')}
        </h1>
        <p className="mt-1 text-sm text-foreground-600">{t('admin.settings.subtitle')}</p>
      </div>

      <div className="rounded-lg border border-background-200 bg-background-50 p-6">
        <h2 className="flex items-center gap-2 font-heading text-base font-semibold text-foreground-950">
          <i className="ri-mail-settings-line text-lg text-primary-700"></i>
          {t('admin.settings.emailSectionTitle')}
        </h2>
        <p className="mt-2 text-sm leading-relaxed text-foreground-600">
          {t('admin.settings.emailSectionSubtitle')}
        </p>

        <form onSubmit={handleUpdateAdminEmail} className="mt-5 space-y-4">
          <div>
            <label className="mb-1.5 block text-sm font-semibold text-foreground-800">
              {t('admin.settings.adminEmailLabel')}
            </label>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <input
                type="email"
                name="admin_email"
                value={newAdminEmail}
                autoComplete="off"
                onChange={(event) => {
                  setNewAdminEmail(event.target.value);
                  setAdminEmailFeedback(null);
                }}
                placeholder={t('admin.login.emailPh')}
                className={baseInput}
              />
              <input
                type="password"
                name="admin_email_password"
                value={adminEmailPassword}
                autoComplete="current-password"
                onChange={(event) => {
                  setAdminEmailPassword(event.target.value);
                  setAdminEmailFeedback(null);
                }}
                placeholder={t('admin.settings.adminEmailPassword')}
                className={baseInput}
              />
            </div>
          </div>
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={adminEmailSaving}
              className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60 dark:text-foreground-950"
            >
              {adminEmailSaving && <i className="ri-loader-4-line animate-spin"></i>}
              {t('admin.settings.adminEmailSave')}
            </button>
          </div>
        </form>
        {feedbackBox(adminEmailFeedback)}

        <div className="mt-6 border-t border-background-200 pt-6">
          <form onSubmit={handleUpdateSupportEmail} className="space-y-4">
            <div>
              <label className="mb-1.5 block text-sm font-semibold text-foreground-800">
                {t('admin.settings.supportEmailLabel')}
              </label>
              <input
                type="email"
                name="support_email"
                value={supportEmail}
                autoComplete="off"
                onChange={(event) => {
                  setSupportEmail(event.target.value);
                  setSupportFeedback(null);
                }}
                placeholder={t('admin.settings.supportEmailPh')}
                className={baseInput}
              />
              <p className="mt-2 text-xs text-foreground-500">
                {t('admin.settings.supportEmailHint')}
              </p>
            </div>
            <div className="flex justify-end">
              <button
                type="submit"
                disabled={supportSaving}
                className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-secondary-500 px-4 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-secondary-600 disabled:cursor-not-allowed disabled:opacity-60 dark:text-foreground-950"
              >
                {supportSaving && <i className="ri-loader-4-line animate-spin"></i>}
                {t('admin.settings.supportEmailSave')}
              </button>
            </div>
          </form>
          {feedbackBox(supportFeedback)}
        </div>
      </div>

      <CodeLinkCard codeLink={codeLink} onSave={onSaveCodeLink} />

      <div className="rounded-lg border border-background-200 bg-background-50 p-6">
        <h2 className="flex items-center gap-2 font-heading text-base font-semibold text-foreground-950">
          <i className="ri-telegram-line text-lg text-primary-700"></i>
          {t('admin.settings.telegramTitle')}
        </h2>
        <p className="mt-2 text-sm leading-relaxed text-foreground-600">
          {t('admin.settings.telegramSubtitle')}
        </p>

        <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center">
          <button
            type="button"
            onClick={() => void handleTestBot()}
            disabled={botSending}
            className="inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-secondary-500 px-4 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-secondary-600 disabled:cursor-not-allowed disabled:opacity-60 dark:text-foreground-950"
          >
            <i className={botSending ? 'ri-loader-4-line animate-spin' : 'ri-send-plane-2-line'}></i>
            {botSending ? t('admin.settings.telegramSending') : t('admin.settings.telegramButton')}
          </button>
          <span className="text-xs text-foreground-500">
            {t('admin.settings.telegramHint')}
          </span>
        </div>

        {feedbackBox(botFeedback)}
      </div>

      <div className="rounded-lg border border-background-200 bg-background-50 p-6">
        <h2 className="flex items-center gap-2 font-heading text-base font-semibold text-foreground-950">
          <i className="ri-lock-password-line text-lg text-primary-700"></i>
          {t('admin.settings.passwordTitle')}
        </h2>

        <form onSubmit={handleSubmit} className="mt-5 space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div>
              <label className="mb-1.5 block text-sm font-semibold text-foreground-800">
                {t('admin.settings.current')}
              </label>
              <input
                type={showPasswords ? 'text' : 'password'}
                value={current}
                autoComplete="current-password"
                onChange={(event) => {
                  setCurrent(event.target.value);
                  setError(null);
                }}
                className={baseInput}
              />
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-semibold text-foreground-800">
                {t('admin.settings.new')}
              </label>
              <input
                type={showPasswords ? 'text' : 'password'}
                value={next}
                autoComplete="new-password"
                onChange={(event) => {
                  setNext(event.target.value);
                  setError(null);
                }}
                className={baseInput}
              />
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-semibold text-foreground-800">
                {t('admin.settings.confirm')}
              </label>
              <input
                type={showPasswords ? 'text' : 'password'}
                value={confirm}
                autoComplete="new-password"
                onChange={(event) => {
                  setConfirm(event.target.value);
                  setError(null);
                }}
                className={baseInput}
              />
            </div>
          </div>

          <button
            type="button"
            onClick={() => setShowPasswords((prev) => !prev)}
            className="inline-flex cursor-pointer items-center gap-1.5 text-xs font-semibold text-foreground-600 transition-colors hover:text-foreground-900"
          >
            <i className={showPasswords ? 'ri-eye-off-line' : 'ri-eye-line'}></i>
            {showPasswords ? t('bankAuth.hidePassword') : t('bankAuth.showPassword')}
          </button>

          {error && (
            <div className="flex items-start gap-2 rounded-md border border-accent-200 bg-accent-50 px-3 py-2.5 text-sm text-accent-900">
              <i className="ri-error-warning-line mt-0.5"></i>
              <span>{error}</span>
            </div>
          )}

          {success && (
            <div className="flex items-start gap-2 rounded-md border border-primary-200 bg-primary-50 px-3 py-2.5 text-sm text-primary-900">
              <i className="ri-checkbox-circle-line mt-0.5"></i>
              <span>{t('admin.settings.success')}</span>
            </div>
          )}

          <div className="flex justify-end">
            <button
              type="submit"
              disabled={submitting}
              className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60 dark:text-foreground-950"
            >
              {submitting && <i className="ri-loader-4-line animate-spin"></i>}
              {t('admin.settings.submit')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}